import { ResponseResult } from '@amzn/gbsa-helios-typescript-client';

/**
 * Violation record describing what's missing on a single question or
 * sub-question for the close-audit gate. Shared between the legacy
 * desktop validator (`src/utils/auditClosureValidationUtils`) and
 * the v2 SDK-shape validator (`computeAuditClosureViolations`).
 */
export interface MandatoryFailViolation {
  questionId: string;
  subQuestionId?: string;
  missingComment: boolean;
  missingEvidence: boolean;
  missingNaReason: boolean;
  /**
   * A data-collection (ERT) sub-question has no captured value while
   * the parent's committed result requires one (PASS / FAIL — an NA
   * parent waives its data subs). Never set for scored Pass/Fail
   * targets: their unansweredness is the `isAllAnswered` gate's job.
   */
  missingResponse?: boolean;
  /** The latest persistence attempt for this question failed. */
  saveFailed?: boolean;
}

/**
 * Checks if a specific sub-question has a violation.
 */
export function hasSubQuestionViolation(
  violations: readonly MandatoryFailViolation[],
  subQuestionId: string,
): boolean {
  return violations.some((v) => v.subQuestionId === subQuestionId);
}

/**
 * Checks if a question has a question-level violation (not sub-question).
 */
export function hasQuestionLevelViolation(violations: MandatoryFailViolation[]): boolean {
  return violations.some((v) => !v.subQuestionId);
}

/**
 * Returns a short description of what's missing for a question, derived
 * directly from the per-violation flags. Names ONLY the fields that are
 * actually missing so mobile users see exactly what to fix.
 *
 * Examples:
 *   "Missing comment"
 *   "Missing 3 comments"
 *   "Missing comment and attachment"
 *   "Missing comment, attachment, and Not Applicable reason"
 *
 * Returns "" when the violation set carries no missing flags.
 */
interface ViolationCounts {
  response: number;
  comment: number;
  evidence: number;
  naReason: number;
  saveFailure: number;
}

function countViolationFlags(violations: MandatoryFailViolation[]): ViolationCounts {
  const counts: ViolationCounts = {
    response: 0,
    comment: 0,
    evidence: 0,
    naReason: 0,
    saveFailure: 0,
  };
  for (const violation of violations) {
    if (violation.missingResponse) counts.response++;
    if (violation.missingComment) counts.comment++;
    if (violation.missingEvidence) counts.evidence++;
    if (violation.missingNaReason) counts.naReason++;
    if (violation.saveFailed) counts.saveFailure++;
  }
  return counts;
}

function buildMissingParts(counts: ViolationCounts): string[] {
  const parts: string[] = [];
  if (counts.response > 0) {
    parts.push(formatMissingPart(counts.response, 'response', 'responses'));
  }
  if (counts.comment > 0) {
    parts.push(formatMissingPart(counts.comment, 'comment', 'comments'));
  }
  if (counts.evidence > 0) {
    parts.push(formatMissingPart(counts.evidence, 'attachment', 'attachments'));
  }
  if (counts.naReason > 0) {
    parts.push(
      formatMissingPart(
        counts.naReason,
        'Not Applicable reason',
        'Not Applicable reasons',
      ),
    );
  }
  return parts;
}

export function getWarningTooltip(violations: MandatoryFailViolation[]): string {
  const counts = countViolationFlags(violations);
  const parts = buildMissingParts(counts);
  if (counts.saveFailure > 0) {
    const saveMessage =
      counts.saveFailure === 1 ? 'Change failed to save' : 'Changes failed to save';
    return parts.length === 0
      ? saveMessage
      : `${saveMessage}; missing ${joinMissingParts(parts)}`;
  }
  return parts.length === 0 ? '' : `Missing ${joinMissingParts(parts)}`;
}

function formatMissingPart(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : `${count} ${plural}`;
}

function joinMissingParts(parts: readonly string[]): string {
  if (parts.length === 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  const head = parts.slice(0, -1).join(', ');
  return `${head}, and ${parts[parts.length - 1]}`;
}

/**
 * Returns a field label with (Required) or (Optional) suffix.
 */
export function getFieldRequirementLabel(
  fieldName: string,
  isRequired?: boolean,
  isFail?: boolean,
): string {
  return `${fieldName} ${isRequired && isFail ? '(Required)' : '(Optional)'}`;
}

/**
 * Determines if the NA reason dropdown should be visible for a given target.
 */
export function shouldShowNaReasonDropdown(
  target: 'main' | 'sub',
  opts: {
    mainResponseResult?: string;
    mainNaReasonId?: string;
    localMainNaReason?: { reasonId: string; label: string };
    subResponseResult?: string;
    subNaReasonId?: string;
    subResponses?: { subQuestionId: string; result?: string; naReasonId?: string }[];
    localNaReasons?: Record<string, { reasonId: string; label: string }>;
  },
): boolean {
  const isNaAll = opts.mainResponseResult === ResponseResult.NA;

  if (target === 'sub') {
    if (opts.subResponseResult !== ResponseResult.NA) return false;
    if (opts.subNaReasonId) return true;
    if (isNaAll) return false;
    if (opts.mainNaReasonId || opts.localMainNaReason) return false;
    return true;
  }

  if (isNaAll) return true;

  const naSubResponses = (opts.subResponses ?? []).filter(
    (s) => s.result === ResponseResult.NA,
  );
  if (naSubResponses.length === 0) return true;

  const localReasons = opts.localNaReasons ?? {};
  const allSubsCovered = naSubResponses.every(
    (s) => !!s.naReasonId || !!localReasons[s.subQuestionId],
  );
  return !allSubsCovered;
}
